import { useLayoutEffect, useState } from 'react'

/** The part of the page the viewer can see: the visual viewport's height and its offset from the layout viewport's top. */
export interface VisibleArea {
  height: number
  top: number
}

/** Where a touch may still scroll while the page is pinned: the chat's messages, and the composer (its caret). */
const SCROLLERS = '.watch-chat__log, .watch-chat__compose'

function readArea(): VisibleArea | null {
  const viewport = typeof window === 'undefined' ? null : window.visualViewport
  if (!viewport) return null
  return { height: Math.round(viewport.height), top: Math.round(viewport.offsetTop) }
}

const sameArea = (a: VisibleArea | null, b: VisibleArea | null) => a?.height === b?.height && a?.top === b?.top

/**
 * While `active` (the viewer types on a phone), the visible part of the page, following the on-screen keyboard: the
 * visual viewport's size and offset, on its `resize` and `scroll` (and the window's resize). iOS Safari keeps the layout
 * viewport the size it was and pans the visual viewport over it to show the field, so a page laid out in the layout
 * viewport slides away under the keyboard; a page pinned to this area (its height, translated by its top) stays in
 * view with the field on top of the keyboard. Android with `interactive-widget=resizes-content` resizes the layout
 * viewport instead, and the area is simply all of it.
 *
 * Meanwhile a touch dragged anywhere but the chat's log and composer doesn't scroll the page (iOS would pan it under the
 * keyboard), and once released the document's scroll goes back to where it was, so closing the keyboard restores the
 * layout exactly. Null while inactive, or where the browser has no `visualViewport` (the page keeps its own height).
 */
export function useVisibleArea(active: boolean): VisibleArea | null {
  const [area, setArea] = useState<VisibleArea | null>(null)
  useLayoutEffect(() => {
    if (!active) return
    const viewport = window.visualViewport
    const scrolledTo = window.scrollY
    const update = () => {
      const next = readArea()
      setArea((current) => (sameArea(current, next) ? current : next))
    }
    const onTouchMove = (event: TouchEvent) => {
      if (event.target instanceof Element && event.target.closest(SCROLLERS)) return
      if (event.cancelable) event.preventDefault()
    }
    update()
    viewport?.addEventListener('resize', update)
    viewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      viewport?.removeEventListener('resize', update)
      viewport?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      document.removeEventListener('touchmove', onTouchMove)
      setArea(null)
      if (window.scrollY !== scrolledTo) window.scrollTo(0, scrolledTo)
    }
  }, [active])
  return active ? area : null
}
